import { useEffect, useState } from "react";
import DashLayout from "../components/DashLayout";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Copy, Trash2, KeyRound, X } from "lucide-react";
import { toast } from "sonner";

export default function ApiKeys() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [label, setLabel] = useState("");
    const [revealed, setRevealed] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/keys");
            setKeys(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/keys", { label });
            setRevealed(data);
            setLabel("");
            setShowCreate(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const revoke = async (id) => {
        if (!confirm("Revoke this key? CLI sessions using it will disconnect.")) return;
        try {
            await api.delete(`/keys/${id}`);
            toast.success("Key revoked");
            load();
        } catch {
            toast.error("Failed");
        }
    };

    const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

    return (
        <DashLayout
            title="API KEYS"
            subtitle="dashboard / keys"
            action={
                <button onClick={() => setShowCreate(true)} className="tf-btn" data-testid="open-create-key-btn">
                    <Plus className="w-4 h-4" /> NEW KEY
                </button>
            }
        >
            <p className="text-sm text-[#A1A1AA] mb-6 max-w-2xl">
                Use API keys to authenticate the <span className="text-[#00FF41] font-mono">synapselink</span> CLI on local machines and CI runners. Treat them like passwords — they are shown <strong>once</strong>.
            </p>

            {loading ? (
                <div className="text-[#52525B] text-sm">[ loading keys... ]</div>
            ) : keys.length === 0 ? (
                <div className="tf-card-accent p-12 text-center">
                    <KeyRound className="w-12 h-12 text-[#00FF41] mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="heading-md mb-2">NO KEYS YET</h3>
                    <p className="text-[#A1A1AA] mb-6">Generate your first key to authenticate the CLI.</p>
                    <button onClick={() => setShowCreate(true)} className="tf-btn inline-flex" data-testid="empty-create-key-btn">
                        <Plus className="w-4 h-4" /> CREATE KEY
                    </button>
                </div>
            ) : (
                <div className="border border-[#27272A]" data-testid="api-keys-list">
                    <div className="grid grid-cols-[1fr_2fr_auto_auto] gap-4 px-5 py-3 border-b border-[#27272A] bg-[#0A0A0A] label-mono">
                        <div>Label</div>
                        <div>Key</div>
                        <div>Last Used</div>
                        <div className="text-right">Actions</div>
                    </div>
                    {keys.map((k) => (
                        <div key={k.id} className={`grid grid-cols-[1fr_2fr_auto_auto] gap-4 px-5 py-3.5 border-b last:border-b-0 border-[#27272A] items-center ${k.revoked ? "opacity-40" : ""}`} data-testid={`key-row-${k.id}`}>
                            <div>
                                <div className="text-[#FAFAFA] text-sm font-mono">{k.label}</div>
                                {k.revoked && <span className="text-[10px] uppercase tracking-widest text-[#FF3B30]">revoked</span>}
                            </div>
                            <div className="font-mono text-xs text-[#A1A1AA] truncate">{k.key}</div>
                            <div className="font-mono text-xs text-[#52525B]">{k.last_used ? new Date(k.last_used).toLocaleString() : "never"}</div>
                            <div className="flex justify-end gap-2">
                                {!k.revoked && (
                                    <button onClick={() => revoke(k.id)} className="tf-btn tf-btn-danger !py-1.5 !px-3" data-testid={`revoke-key-${k.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" /> REVOKE
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreate && (
                <Modal onClose={() => setShowCreate(false)} title="GENERATE API KEY">
                    <form onSubmit={create} className="space-y-6">
                        <div>
                            <div className="label-mono mb-1">LABEL</div>
                            <div className="flex items-center border-b-2 border-[#27272A] focus-within:border-[#00FF41]">
                                <span className="text-[#52525B] mr-2">{">"}</span>
                                <input className="bg-transparent flex-1 py-2 outline-none font-mono text-[#FAFAFA]" placeholder="laptop · ci · prod-bot" value={label} onChange={(e) => setLabel(e.target.value)} required data-testid="new-key-label-input" />
                            </div>
                        </div>
                        <button type="submit" className="tf-btn w-full justify-center" data-testid="new-key-submit">
                            GENERATE KEY
                        </button>
                    </form>
                </Modal>
            )}

            {revealed && (
                <Modal onClose={() => setRevealed(null)} title="YOUR NEW KEY · COPY IT NOW">
                    <p className="text-sm text-[#FF9900] mb-4">▸ This is the only time the full key will be shown.</p>
                    <div className="bg-[#050505] border border-[#00FF41] p-4 font-mono text-[#00FF41] text-sm break-all mb-4" data-testid="revealed-key-value">
                        {revealed.key}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => copy(revealed.key)} className="tf-btn flex-1 justify-center" data-testid="copy-revealed-key">
                            <Copy className="w-4 h-4" /> COPY KEY
                        </button>
                        <button onClick={() => setRevealed(null)} className="tf-btn tf-btn-ghost" data-testid="close-revealed-key">
                            DONE
                        </button>
                    </div>
                </Modal>
            )}
        </DashLayout>
    );
}

function Modal({ children, onClose, title }) {
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose}>
            <div className="tf-card-accent w-full max-w-lg p-8 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-[#52525B] hover:text-[#FAFAFA]">
                    <X className="w-5 h-5" />
                </button>
                <div className="label-mono text-[#00FF41] mb-2">// keys</div>
                <h3 className="heading-md mb-6">{title}</h3>
                {children}
            </div>
        </div>
    );
}
